import { getRuntimeConfig } from '../config/runtimeConfig';

/**
 * Clientul pentru rutele de administrare.
 *
 * E singura parte a aplicației care vorbește cu `/admin/*`. Nu are nimic în
 * comun cu biletul de acces: sesiunea de admin e un token separat, cu altă
 * audiență, iar backendul nu acceptă unul în locul celuilalt.
 */

export interface AdminSession {
  token: string;
  /** Secunde. După ele, orice cerere întoarce 401 și se cere parola din nou. */
  expiresIn: number;
}

/** Ce se poate ști despre o cheie fără să o poți folosi. */
export interface AccessKeyView {
  id: number;
  name: string;
  /** Începutul cheii, în clar — cât să o recunoști în listă. */
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** Răspunsul la generare. `key` apare o singură dată, aici. */
export interface IssuedAccessKey extends AccessKeyView {
  key: string;
}

/**
 * Invitația pe care o bate cineva în telefon.
 *
 * Nu conține nicio cheie, și asta e intenționat: cheia se naște abia când
 * telefonul folosește codul, deci nu există niciun moment în care una
 * utilizabilă să aștepte pe server.
 */
export interface PairingInvite {
  code: string;
  /** Secunde până expiră. */
  expiresIn: number;
  name: string;
}

/** Un cod încă valabil, așa cum apare în listă. Codul însuși nu se mai poate afla. */
export interface PendingPairing {
  id: number;
  name: string;
  expiresAt: string;
}

export class AdminError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AdminError';
  }
}

function base(): string {
  return getRuntimeConfig().apiBaseUrl.replace(/\/$/, '');
}

/**
 * Mesaje pe înțelesul cuiva care administrează, nu coduri.
 *
 * `404` nu înseamnă „rută greșită”, ci că backendul pornește fără
 * `ADMIN_PASSWORD` — panoul e închis din configurare, nu stricat.
 */
function messageFor(status: number, onLogin: boolean): string {
  if (status === 404 && onLogin)
    return 'Panoul nu e configurat pe backend (lipsește ADMIN_PASSWORD).';
  if (status === 401) return onLogin ? 'Parolă greșită.' : 'Sesiunea a expirat.';
  if (status === 429) return 'Prea multe încercări. Așteaptă un minut.';
  if (status === 0) return 'Serverul nu răspunde.';
  return `Stația a răspuns cu eroare (${status}).`;
}

async function request<T>(path: string, init: RequestInit, token?: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (token !== undefined) headers.Authorization = `Bearer ${token}`;
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${base()}${path}`, { ...init, headers });
  } catch {
    throw new AdminError(messageFor(0, token === undefined), 0);
  }

  if (!res.ok) {
    throw new AdminError(messageFor(res.status, token === undefined), res.status);
  }

  return (await res.json()) as T;
}

export function login(password: string): Promise<AdminSession> {
  return request<AdminSession>('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function listKeys(token: string): Promise<AccessKeyView[]> {
  return request<AccessKeyView[]>('/admin/keys', { method: 'GET' }, token);
}

export function createKey(token: string, name: string): Promise<IssuedAccessKey> {
  return request<IssuedAccessKey>(
    '/admin/keys',
    { method: 'POST', body: JSON.stringify({ name }) },
    token,
  );
}

/**
 * Șterge o cheie. Rândul dispare din tabel, nu rămâne marcat ca revocat:
 * o cheie tăiată nu se poate reactiva, deci n-are ce păzi în listă.
 *
 * Răspunsul e cheia ștearsă, ca panoul să poată spune ce anume a dispărut.
 */
export function deleteKey(token: string, id: number): Promise<AccessKeyView> {
  return request<AccessKeyView>(`/admin/keys/${id}`, { method: 'DELETE' }, token);
}

/** Emite codul de împerechere pentru un dispozitiv nou. */
export function createPairing(token: string, name: string): Promise<PairingInvite> {
  return request<PairingInvite>(
    '/admin/pairings',
    { method: 'POST', body: JSON.stringify({ name }) },
    token,
  );
}

/**
 * Codurile care încă așteaptă un telefon.
 *
 * Backendul mătură singur ce a expirat, deci lista asta conține numai coduri
 * pe care le mai poate folosi cineva.
 */
export function listPairings(token: string): Promise<PendingPairing[]> {
  return request<PendingPairing[]>('/admin/pairings', { method: 'GET' }, token);
}

/** Anulează un cod nefolosit, înainte să expire de la sine. */
export function deletePairing(token: string, id: number): Promise<{ id: number }> {
  return request<{ id: number }>(`/admin/pairings/${id}`, { method: 'DELETE' }, token);
}
